import { useEffect, useState } from "react";
import type { Me } from "@ai-rpg/protocol";
import { getMe } from "../api.ts";

type Usage = NonNullable<Me["usage"]>;

/** How often the meter looks again, in milliseconds: play moves it slowly. */
const LOOK_AGAIN = 60_000;
/** With this share of the month's play left or less, the meter says so in the accent. */
const LOW = 20;

const day = (iso: string) => new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "long" });

/** What the meter says in words: how much is left, and when it is full again. */
export const usageWords = (usage: Usage) =>
  `${100 - usage.percent}% of this month's play left. It is full again on ${day(usage.renews)}.`;

/** The bar itself: as full as there is play left. */
export function Tank({ usage, wide = false }: { usage: Usage; wide?: boolean }) {
  const left = 100 - usage.percent;
  return (
    <span className={wide ? "tank tank-wide" : "tank"} data-low={left <= LOW} aria-hidden="true">
      <span className="tank-fill" style={{ width: `${left}%` }} />
    </span>
  );
}

/**
 * How much of the month's play an account has left, as a battery's meter shows its charge: a small bar and a
 * figure, in the bar across the top. Nothing for an account with no limit. It looks again every minute, and
 * whenever the window is come back to.
 */
export function Meter() {
  const [usage, setUsage] = useState<Usage | null>(null);
  useEffect(() => {
    let gone = false;
    const look = () =>
      void getMe()
        .then((me) => !gone && setUsage(me.usage ?? null))
        .catch(() => undefined);
    look();
    const again = window.setInterval(look, LOOK_AGAIN);
    window.addEventListener("focus", look);
    return () => {
      gone = true;
      window.clearInterval(again);
      window.removeEventListener("focus", look);
    };
  }, []);
  if (!usage) return null;
  const left = 100 - usage.percent;
  return (
    <span className="meter" data-low={left <= LOW} title={usageWords(usage)} role="img" aria-label={usageWords(usage)}>
      <span className="meter-label">Play</span>
      <Tank usage={usage} />
      <span className="meter-figure">{left}%</span>
    </span>
  );
}
